"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Cpu, Layers, Rocket, Crosshair, Code as Code2, SlidersHorizontal as Sliders, Timer, ShieldCheck, Lock, Trophy, Globe, CircleAlert as AlertCircle, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
interface Capability {
  id: number;
  icon: React.ComponentType<{
    className?: string;
  }>;
  num: string;
  vi: {
    title: string;
    desc: string;
  };
  en: {
    title: string;
    desc: string;
  };
}
const CAPABILITIES: Capability[] = [{
  id: 1,
  icon: Cpu,
  num: "01",
  vi: {
    title: "Cơ cấu nhiệm vụ",
    desc: "Thiết kế các cơ cấu chấp hành linh hoạt, tối ưu cho từng thử thách đặc thù."
  },
  en: {
    title: "Mission mechanism",
    desc: "Designing flexible end-effectors optimized for specialized task execution."
  }
}, {
  id: 2,
  icon: Layers,
  num: "02",
  vi: {
    title: "Cơ cấu thụ động",
    desc: "Ứng dụng động học thụ động giúp tiết kiệm động cơ và tăng tốc độ xử lý."
  },
  en: {
    title: "Passive mechanisms",
    desc: "Applying passive kinematics to conserve motor ports and increase speed."
  }
}, {
  id: 3,
  icon: Rocket,
  num: "03",
  vi: {
    title: "Đẩy, nâng, thả, bắn",
    desc: "Thành thạo 4 nhóm thao tác tác động lực chính xác trên sân thi đấu."
  },
  en: {
    title: "Push/lift/drop/launch",
    desc: "Mastering four force execution groups for precision arena maneuvers."
  }
}, {
  id: 4,
  icon: Crosshair,
  num: "04",
  vi: {
    title: "Căn chỉnh",
    desc: "Kỹ thuật định vị lại robot bằng thành sân, cảm biến và mốc tham chiếu."
  },
  en: {
    title: "Alignment",
    desc: "Robot repositioning techniques using walls, sensors, and map references."
  }
}, {
  id: 5,
  icon: Code2,
  num: "05",
  vi: {
    title: "Hàm nhiệm vụ / My Blocks",
    desc: "Mô-đun hóa chương trình complex giúp mã nguồn gọn gàng và tái sử dụng."
  },
  en: {
    title: "Mission functions / My Blocks",
    desc: "Modularizing complex routines for clean, reusable execution blocks."
  }
}, {
  id: 6,
  icon: Sliders,
  num: "06",
  vi: {
    title: "Biến trạng thái",
    desc: "Quản lý dữ liệu tiến trình sân thi đấu bằng biến số logic thông minh."
  },
  en: {
    title: "State variables",
    desc: "Managing live match state and logical decisions through dynamic variables."
  }
}, {
  id: 7,
  icon: Timer,
  num: "07",
  vi: {
    title: "Chiến thuật 150 giây",
    desc: "Lập trình & phân bổ lộ trình chạy điểm tối đa trong thời gian quy định."
  },
  en: {
    title: "150-second strategy",
    desc: "Path planning and time allocation to maximize points under strict limits."
  }
}, {
  id: 8,
  icon: ShieldCheck,
  num: "08",
  vi: {
    title: "Độ ổn định",
    desc: "Tối ưu thuật toán giảm thiểu sai số vật lý khi robot vận hành thực tế."
  },
  en: {
    title: "Reliability",
    desc: "Algorithm optimization to minimize cumulative mechanical variance."
  }
}, {
  id: 9,
  icon: Lock,
  num: "09",
  vi: {
    title: "Nhiệm vụ bí mật",
    desc: "Rèn luyện tư duy ứng biến nhanh trước thử thách phát sinh tại chỗ."
  },
  en: {
    title: "Secret mission",
    desc: "Training rapid problem-solving for unexpected on-site challenge reveals."
  }
}, {
  id: 10,
  icon: Trophy,
  num: "10",
  vi: {
    title: "Mô phỏng đấu trường",
    desc: "Trải nghiệm áp lực thi đấu thực tế theo thể thức chuẩn quốc tế."
  },
  en: {
    title: "Simulated tournament",
    desc: "Experiencing live match pressure under international arena standards."
  }
}];
export default function Hp6Spotlight() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const saved = localStorage.getItem("roboto_lang");
    if (saved === "vi" || saved === "en") {
      setLang(saved);
    }
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail === "vi" || customEvent.detail === "en") {
        setLang(customEvent.detail);
      } else {
        const updated = localStorage.getItem("roboto_lang");
        if (updated === "vi" || updated === "en") {
          setLang(updated);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const switchLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    localStorage.setItem("roboto_lang", newLang);
    window.dispatchEvent(new CustomEvent("roboto-lang-change", {
      detail: newLang
    }));
  };
  return <section id="hp6spotlight" data-nav="dark" className="dark relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Background Circuit Grid Decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <svg className="w-full h-full stroke-foreground/10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hp6-grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" strokeWidth="0.8" />
              <circle cx="40" cy="40" r="1.5" className="fill-foreground/20" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hp6-grid-pattern)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Row with Language Switcher */}
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 pb-12 border-b border-border/40 mb-12">
          <div className="text-center md:text-left space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 border-primary/40 text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
                <Sparkles className="size-3.5 text-primary" />
                {lang === "vi" ? "Đỉnh cao lộ trình cốt lõi" : "Peak of the Core Pathway"}
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-serif">
              {lang === "vi" ? "HP6 — ROBOT NHIỆM VỤ" : "HP6 — MISSION ROBOT"}
            </h2>

            <p className="text-lg sm:text-xl text-muted-foreground font-medium">
              {lang === "vi" ? "Từ việc điều khiển robot đến giải quyết nhiệm vụ có chiến lược." : "From controlling a robot to solving missions strategically."}
            </p>
          </div>

          {/* Language Toggle Control */}
          <div className="shrink-0 flex items-center bg-card/80 border border-border p-1 rounded-full shadow-xs">
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => switchLanguage("vi")} className="rounded-full text-xs font-semibold px-3 py-1 h-7">
              <Globe className="size-3.5 mr-1" />
              TIẾNG VIỆT
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => switchLanguage("en")} className="rounded-full text-xs font-semibold px-3 py-1 h-7">
              ENGLISH
            </Button>
          </div>
        </div>

        {/* HP6 Mission Robot Technical Showcase Card */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="relative bg-card/70 border border-border/80 rounded-2xl overflow-hidden p-6 sm:p-8 mb-12 shadow-xl backdrop-blur-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 relative group">
              <div className="relative rounded-xl overflow-hidden border border-primary/30 bg-background/80 shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hp6_mission_robot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hp6_mission_robot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/hp6_mission_robot.png-wvc-sizes" alt={lang === "vi" ? "Robot nhiệm vụ HP6 với cơ cấu nâng, đẩy, thả" : "HP6 mission robot featuring push, lift, and drop task mechanisms"} loading="lazy" className="w-full h-[320px] sm:h-[380px] object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent pointer-events-none" />

                {/* Technical Specification Badges & Overlays */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <Badge className="bg-primary/90 text-primary-foreground border-none text-[11px] font-mono px-2.5 py-1 shadow-xs">
                    HP6-SPEC // TASK-BOT
                  </Badge>
                  <Badge variant="outline" className="bg-background/80 backdrop-blur-md border-primary/40 text-foreground text-[11px] font-mono px-2.5 py-1">
                    {lang === "vi" ? "CƠ CẤU NHIỆM VỤ 3 TRỤC" : "3-AXIS TASK MECHANISM"}
                  </Badge>
                </div>

                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end text-xs font-mono">
                  <div className="bg-background/90 backdrop-blur-md border border-border/60 rounded-lg p-3 max-w-xs shadow-md">
                    <span className="text-primary font-bold block mb-0.5">
                      {lang === "vi" ? "Cơ cấu Nâng / Đẩy / Thả:" : "Lift / Push / Drop Mechanism:"}
                    </span>
                    <span className="text-muted-foreground text-[11px] leading-snug">
                      {lang === "vi" ? "Tối ưu hóa thao tác gắp thả linh kiện & vượt chướng ngại vật đấu trường." : "Optimized for arena component manipulation and obstacle navigation."}
                    </span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 bg-primary/20 backdrop-blur-md border border-primary/40 text-primary px-3 py-1.5 rounded-lg text-[11px] font-bold">
                    <Sparkles className="size-3.5" />
                    <span>ENJOY AI READY</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-secondary" />
                <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold">
                  {lang === "vi" ? "Mô hình thi đấu thực chiến" : "Arena Competition Model"}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground leading-snug">
                {lang === "vi" ? "Kiến trúc Robot Nhiệm vụ Cốt lõi" : "Core Mission Robot Architecture"}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {lang === "vi" ? "Mẫu robot HP6 tích hợp cụm tay gắp thụ động và mô-đun động cơ hạ thủy lực, cho phép thực hiện chuỗi 10 nhiệm vụ liên hoàn trên sa bàn thi đấu tiêu chuẩn." : "The HP6 robot model integrates passive linkage manipulators and motorized actuation modules, enabling execution of complex 10-task routines on standard tournament arenas."}
              </p>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="p-3 rounded-lg bg-background/60 border border-border/60">
                  <span className="text-xs font-mono text-muted-foreground block">
                    {lang === "vi" ? "Độ chính xác" : "Precision"}
                  </span>
                  <span className="text-base font-bold text-foreground font-mono">
                    ±1.5mm
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-background/60 border border-border/60">
                  <span className="text-xs font-mono text-muted-foreground block">
                    {lang === "vi" ? "Cơ cấu tác động" : "Actuation"}
                  </span>
                  <span className="text-base font-bold text-primary font-mono">
                    {lang === "vi" ? "Đa nhiệm" : "Multi-Task"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 10-Item Capability Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5 mb-12">
          {CAPABILITIES.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: 16,
            scale: 0.97
          }} whileInView={{
            opacity: 0.99,
            y: 0,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.05
          }} className="group relative bg-card/60 backdrop-blur-sm border border-border/80 hover:border-primary/50 rounded-xl p-5 flex flex-col justify-between transition-all duration-200 hover:shadow-lg hover:-translate-y-1">
                {/* Circuit Corner Accent */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary/20 group-hover:border-primary/60 rounded-tr-xl transition-colors" aria-hidden="true" />

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <Icon className="size-5" />
                    </div>
                    <span className="text-xs font-mono font-bold text-muted-foreground/60 tracking-wider">
                      {item.num}
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-foreground mb-2 line-clamp-2 leading-snug">
                    {lang === "vi" ? item.vi.title : item.en.title}
                  </h3>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {lang === "vi" ? item.vi.desc : item.en.desc}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-border/30 flex items-center justify-between text-[11px] font-semibold text-primary/80 group-hover:text-primary transition-colors">
                  <span>{lang === "vi" ? "Năng lực HP6" : "HP6 Core Skill"}</span>
                  <span className="size-1.5 rounded-full bg-secondary" />
                </div>
              </motion.div>;
        })}
        </div>

        {/* Emphasis Callout Box */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }}>
          <Alert className="bg-card/90 border-2 border-primary/40 rounded-xl p-6 shadow-md relative overflow-hidden">
            <div className="absolute -left-12 -bottom-12 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" aria-hidden="true" />
            <AlertCircle className="size-6 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1.5 pl-1">
              <AlertTitle className="text-base font-bold text-foreground">
                {lang === "vi" ? "Lưu ý quan trọng về định hướng khóa học" : "Important Course Orientation Note"}
              </AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground leading-relaxed">
                {lang === "vi" ? "HP6 mô phỏng tinh thần Invention Trials của ENJOY AI và là bước chuẩn bị cho giai đoạn luyện thi ENJOY AI riêng biệt sau đó. HP6 không phải là HP7, và việc hoàn thành HP6 không đồng nghĩa với cam kết giải thưởng hay thành tích thi đấu." : "HP6 mirrors the spirit of ENJOY AI's Invention Trials and prepares students for the separate ENJOY AI preparation stage that follows. HP6 is not HP7, and completing it does not guarantee any award or competition result."}
              </AlertDescription>
            </div>
          </Alert>
        </motion.div>
      </div>
    </section>;
}